import { useCallback, useMemo, useState, lazy, Suspense } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { useRegisterBreadcrumb } from "@/contexts/BreadcrumbContext";
import { Link, useNavigate, useLocation } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
// Lazy-load the analytics tab so recharts (~100KB+) is fetched only when the
// user opens Analytics, not on every login (the SPA lands on Overview).
const DashboardAnalytics = lazy(() => import("@/pages/DashboardAnalytics"));
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BfExpiringWidget } from "@/components/dashboard/BfExpiringWidget";
import { AttentionItem } from "@/components/dashboard/AttentionItem";
import { GroupOverview } from "@/components/dashboard/GroupOverview";
import { MyMetricsBlock } from "@/components/dashboard/MyMetricsBlock";
import { useOccupancySparkline } from "@/components/dashboard/useOccupancySparkline";
import {
  capacityAttentionHref,
  DASHBOARD_CAPACITY_ATTENTION_ROWS,
} from "@/components/dashboard/capacityAttention";
import { MetricPanelCard } from "@/components/cockpit/metrics/MetricPanelCard";
import { MetricSparkline } from "@/components/cockpit/metrics/MetricSparkline";
import { analyticsMetricHref } from "@/components/cockpit/metrics/metricLinks";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { SetupChecklistCard } from "@/components/dashboard/SetupChecklistCard";
import { LiveStaffingCard } from "@/components/dashboard/LiveStaffingCard";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard, StatGrid } from "@/components/ui/stat-card";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  formatDateDisplay,
  formatDateMedium,
  formatDecimal,
  formatNumber,
  formatPercent,
  formatWeekday,
} from "@/lib/format";
import { formatMoneyCents } from "@/lib/money";
import { formatOrgTime } from "@/utils/dateUtils";
import { isReconciliationEnabled } from "@/lib/featureFlags";
import {
  occupancyAccent,
  occupancyFigures,
  toOccupancyGroup,
  type OccupancyAccent,
} from "@/lib/analytics/occupancy";
import { useCurrency } from "@/hooks/useCurrency";
import { useExpiringBeitragsfaktoren } from "@/hooks/useExpiringBeitragsfaktoren";
import { SUBSIDY_WIDGET_DAYS, useDashboardSources } from "@/hooks/useDashboardSources";
import { NAV_ITEMS, navItemLabel } from "@/lib/navigation";
import type { CapacityAttentionItem } from "@/types/capacity";
import {
  UsersIcon,
  ShieldCheckIcon,
  BarChart3Icon,
  ClipboardListIcon,
  AlertTriangleIcon,
  MessageSquareIcon,
  CalendarIcon,
  ClockIcon,
  UserPlusIcon,
  UserMinusIcon,
  CheckCircle2Icon,
  UtensilsIcon,
  ActivityIcon,
  BabyIcon,
  BanknoteIcon,
  CalendarClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FilterIcon,
  Building2Icon,
  XIcon,
} from "lucide-react";
import { format, addDays, subDays, subMonths, isToday as isDateToday, parseISO } from "date-fns";
import { useAuth } from "@clerk/clerk-react";
import { usePendingCount } from "@/hooks/useWorkflows";
import { useUnreadCount } from "@/hooks/useMessages";
import { useCountPendingAbsences } from "@/hooks/useEmployeeAbsences";
import { useSubmittedTimeEntriesCount } from "@/hooks/useTimeTracking";
import { useEffectiveSettings } from "@/hooks/settings/useEffectiveSettings";
import { useResolveServiceName } from "@/hooks/useResolveServiceName";
import { UpcomingBirthdays } from "@/components/dashboard/UpcomingBirthdays";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useCanOpen } from "@/hooks/useCanOpen";
import { useSetupChecklist } from "@/hooks/useSetupChecklist";
import { useChildcares } from "@/hooks/settings/useChildcare";
import { useGroups } from "@/hooks/settings/useGroups";
import { useOverdueInvoiceCount } from "@/hooks/useDashboard";
import { FormulaTip } from "@/components/ui/formula-tip";
import { FORMULA } from "@/lib/analytics/formulas";
import { inProgressSettlingIn } from "@/lib/analytics/settlingInProgress";
import { formatLocalDate } from "@/utils/dateUtils";

/** Birthdays the Upcoming card lists, as on the phone (#2175 C7). */
const BIRTHDAY_WINDOW_DAYS = 30;
/** Subsidy dates the attention card counts: within 60 days, as on the phone (#2175 C4). */
const SUBSIDY_ATTENTION_DAYS = 60;

const OCCUPANCY_TONE: Record<OccupancyAccent, StatusTone | undefined> = {
  good: "success",
  warn: "warning",
  bad: "danger",
  neutral: undefined,
};

// --- Main Dashboard ---

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const { user } = useUser();
  const { orgId } = useAuth();
  // The Analytics tab is a cockpit-powered (owner-only) surface (issue #906). Non-admins
  // never see the tab; the birthday Overview widget (also cockpit-powered) is gated too.
  const isAdmin = useIsAdmin();
  // Each figure is fetched only when the user may open the page it links to, so an
  // employee without a module issues no request that would come back 403.
  const canOpen = useCanOpen();
  const canCapacity = canOpen("/capacity");
  const canShifts = canOpen("/shifts");
  const canWorkflows = canOpen("/workflows");
  const canMessages = canOpen("/messaging");
  const canBilling = canOpen("/billing");
  const canTimeTracking = canOpen("/time-tracking");
  const canKitchen = canOpen("/kitchen");
  const canActivities = canOpen("/activities");
  const canSettlingIn = canOpen("/settling-in");
  const canCalendar = canOpen("/calendar");

  // A non-admin who lands on /dashboard/analytics (e.g. a bookmarked URL) is coerced back to
  // the Overview tab; the route itself is also guarded by RequireAdminRoute in App.tsx.
  const activeTab =
    location.pathname === "/dashboard/analytics" && isAdmin ? "analytics" : "overview";
  useRegisterBreadcrumb(
    activeTab === "analytics" ? i18n._({ id: "cockpit.analytics", message: "Analytics" }) : undefined,
  );
  const handleTabChange = (value: string) => {
    if (value === "analytics") {
      navigate("/dashboard/analytics");
    } else {
      navigate("/");
    }
  };

  // Date navigation state
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const selectedDateStr = useMemo(() => format(selectedDate, "yyyy-MM-dd"), [selectedDate]);
  const isSelectedToday = isDateToday(selectedDate);

  const handlePrevDay = () => setSelectedDate((d) => subDays(d, 1));
  const handleNextDay = () => setSelectedDate((d) => addDays(d, 1));
  const handleToday = () => setSelectedDate(new Date());
  const handleDatePick = (date: Date | undefined) => {
    if (date) setSelectedDate(date);
  };

  // Filter state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedChildcareId, setSelectedChildcareId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const { data: childcaresData } = useChildcares({ limit: 100, offset: 0 });
  const { data: groupsData } = useGroups(orgId || "", 100, 0);

  const childcares = useMemo(() => childcaresData?.data || [], [childcaresData?.data]);
  const allGroups = useMemo(() => groupsData || [], [groupsData]);

  const filteredGroupsForDropdown = useMemo(() => {
    if (!selectedChildcareId) return allGroups;
    return allGroups.filter((g) => g.childcare_id === selectedChildcareId);
  }, [allGroups, selectedChildcareId]);

  const selectedChildcareName = useMemo(() => {
    if (!selectedChildcareId) return null;
    return childcares.find((c) => c.id === selectedChildcareId)?.name || null;
  }, [childcares, selectedChildcareId]);

  const selectedGroupName = useMemo(() => {
    if (!selectedGroupId) return null;
    return allGroups.find((g) => g.id === selectedGroupId)?.name || null;
  }, [allGroups, selectedGroupId]);

  const hasActiveFilters = !!selectedChildcareId || !!selectedGroupId;

  const handleChildcareChange = (value: string) => {
    if (value === "__all__") {
      setSelectedChildcareId(null);
      setSelectedGroupId(null);
    } else {
      setSelectedChildcareId(value);
      if (selectedGroupId) {
        const group = allGroups.find((g) => g.id === selectedGroupId);
        if (group?.childcare_id !== value) setSelectedGroupId(null);
      }
    }
  };

  const handleGroupChange = (value: string) => {
    setSelectedGroupId(value === "__all__" ? null : value);
  };

  const handleClearAllFilters = () => {
    setSelectedChildcareId(null);
    setSelectedGroupId(null);
  };

  // --- Data (keyed to selectedDate) ---
  // P1 (#769): the Overview tab's queries must NOT fire when the Analytics tab is
  // active. They are gated on `isOverview` so opening Analytics performs zero
  // Overview fetches.
  const isOverview = activeTab === "overview";
  // The tiles' sources: one summary request for an admin (#2193 A).
  const sources = useDashboardSources({
    day: selectedDateStr,
    isToday: isSelectedToday,
    isOverview,
    isAdmin,
    can: {
      capacity: canCapacity,
      shifts: canShifts,
      workflows: canWorkflows,
      billing: canBilling,
      activities: canActivities,
      settlingIn: canSettlingIn,
      calendar: canCalendar,
    },
    reconciliation: isReconciliationEnabled(),
    childcareId: selectedChildcareId,
  });
  const {
    capacity,
    coverage,
    workflowStats,
    debtorSummary,
    understaffedAhead,
    capacityAttention,
    settlingIn,
    changes: changesSummary,
    events: upcomingEvents,
    activities: todayActivities,
    birthdays,
    aheadRange,
  } = sources;
  // Settling-in plans running today: the management app's rule (#2175 B4) on
  // the same page of open plans, so both apps show one number.
  const settlingInRunning = useMemo(
    () => inProgressSettlingIn(settlingIn.data?.items, formatLocalDate(new Date())).length,
    [settlingIn.data],
  );
  const resolveServiceName = useResolveServiceName();
  const pendingCount = usePendingCount({ enabled: isOverview && canWorkflows });
  // The same queries as the sidebar badges, so both always show one number.
  const unreadMessages = useUnreadCount("employee", { enabled: isOverview && canMessages, audience: "all" });
  const pendingAbsences = useCountPendingAbsences({ forMe: true, enabled: isOverview && canShifts });
  const overdueInvoices = useOverdueInvoiceCount({ enabled: isOverview && canBilling });
  // #1903: an organisation that does not track time has no timesheets to
  // approve. The count waits for the settings so it never fires for such an org.
  const effectiveSettings = useEffectiveSettings();
  const timeTrackingOff = effectiveSettings.data?.timeTrackingEnabled === false;
  const submittedTimeEntries = useSubmittedTimeEntriesCount({
    enabled: isOverview && canTimeTracking && !effectiveSettings.isPending && !timeTrackingOff,
  });
  const setup = useSetupChecklist({ enabled: isOverview && isAdmin });
  const { currency } = useCurrency();
  // The starred metrics show the last full month before the selected day, the
  // management app's My metrics period (D14): the same numbers in both apps.
  // Their sparklines still cover twelve months (chartWindowFrom).
  const lastFullMonth = useMemo(() => format(subMonths(selectedDate, 1), "yyyy-MM"), [selectedDate]);
  // The twelve months ending with the selected day's month (the hub's "Running year").
  const trendWindow = useMemo(
    () => ({ from: format(subMonths(selectedDate, 11), "yyyy-MM"), to: format(selectedDate, "yyyy-MM") }),
    [selectedDate],
  );
  const metricScope = useMemo(
    () => ({ childcare_id: selectedChildcareId || undefined, group_id: selectedGroupId || undefined }),
    [selectedChildcareId, selectedGroupId],
  );
  // The subsidy widget's own query (same key), filled by the summary, so the row costs no request.
  const expiringSubsidy = useExpiringBeitragsfaktoren(SUBSIDY_WIDGET_DAYS, { enabled: sources.subsidyReady });

  // --- Derived data ---

  // Capacity KPIs — filter and sort by sort_order from group definitions
  const groups = useMemo(() => {
    const allCapacityGroups = capacity.data?.groups ?? [];
    const filtered = (!selectedChildcareId && !selectedGroupId)
      ? allCapacityGroups
      : allCapacityGroups.filter((cg) => {
          if (selectedGroupId) return cg.group_id === selectedGroupId;
          if (selectedChildcareId) {
            const groupDef = allGroups.find((g) => g.id === cg.group_id);
            return groupDef?.childcare_id === selectedChildcareId;
          }
          return true;
        });
    return [...filtered].sort((a, b) => {
      const aOrder = allGroups.find((g) => g.id === a.group_id)?.sort_order ?? 999;
      const bOrder = allGroups.find((g) => g.id === b.group_id)?.sort_order ?? 999;
      return aOrder - bOrder;
    });
  }, [capacity.data?.groups, selectedChildcareId, selectedGroupId, allGroups]);
  // The location of each group, for the sections and the scope.
  const groupsWithLocation = useMemo(
    () =>
      groups.map((g) => ({
        ...g,
        childcare_id: g.childcare_id ?? allGroups.find((def) => def.id === g.group_id)?.childcare_id,
      })),
    [groups, allGroups],
  );
  const childcareOptions = useMemo(
    () => childcares.map((c) => ({ id: c.id || "", name: c.name || "" })),
    [childcares],
  );
  // One occupancy figure, as on the phone (#2175 C1, D3): weighted children over
  // weighted places of the groups in scope, never the mean of percentages.
  const occupancy = useMemo(() => occupancyFigures(groupsWithLocation.map(toOccupancyGroup)), [groupsWithLocation]);
  const scopeGroupIds = useMemo(
    () => (hasActiveFilters ? new Set(groups.map((g) => g.group_id ?? "")) : null),
    [hasActiveFilters, groups],
  );
  const inScope = useCallback((id: string) => !scopeGroupIds || scopeGroupIds.has(id), [scopeGroupIds]);
  // Weighted like the figure: each group by its weighted places (the phone's C12).
  const weightedPlaces = useMemo(
    () =>
      new Map(
        (capacity.data?.groups ?? [])
          .filter((g) => (g.max_weighted_headcount ?? 0) > 0)
          .map((g) => [g.group_id ?? "", g.max_weighted_headcount as number]),
      ),
    [capacity.data?.groups],
  );
  const occupancySpark = useOccupancySparkline(sources.occupancyRange, inScope, weightedPlaces);
  const totalHeadcount = groups.reduce((sum, g) => sum + (g.headcount ?? 0), 0);
  const totalMaxHeadcount = groups.reduce((sum, g) => sum + (g.max_headcount ?? 0), 0);
  const totalWeightedHeadcount = groups.reduce((sum, g) => sum + (g.weighted_headcount ?? g.headcount ?? 0), 0);
  const totalMaxWeightedHeadcount = groups.reduce((sum, g) => sum + (g.max_weighted_headcount ?? g.max_headcount ?? 0), 0);

  // Arrived of expected: the overview sets it for today only. Unfiltered, the org
  // totals count a child on two rosters once; filtered, the groups are summed.
  const arrivals = useMemo(() => {
    if (!isSelectedToday || !capacity.data) return null;
    if (!hasActiveFilters) {
      const { total_present: present, total_expected: expected } = capacity.data;
      return typeof present === "number" && typeof expected === "number" ? { present, expected } : null;
    }
    let present = 0;
    let expected = 0;
    for (const g of groups) {
      if (typeof g.present_count !== "number") return null;
      present += g.present_count;
      expected += g.expected_count ?? 0;
    }
    return { present, expected };
  }, [isSelectedToday, capacity.data, hasActiveFilters, groups]);

  // Coverage KPIs (the planned roster for the selected date), narrowed to the
  // location/group filter like every other tile and like the management app
  // (it summed every group before, whatever the filter).
  const coverageGroups = useMemo(
    () =>
      (coverage.data?.groups ?? []).filter((g) => {
        if (selectedGroupId) return g.group_id === selectedGroupId;
        if (selectedChildcareId) {
          return allGroups.find((def) => def.id === g.group_id)?.childcare_id === selectedChildcareId;
        }
        return true;
      }),
    [coverage.data?.groups, selectedChildcareId, selectedGroupId, allGroups],
  );
  const totalStaffOnDuty = coverageGroups.reduce((sum, g) => sum + (g.scheduled_staff ?? 0), 0);
  const totalStaffRequired = coverageGroups.reduce((sum, g) => sum + (g.required_staff ?? 0), 0);
  const staffingNotConfigured =
    coverageGroups.length > 0 && coverageGroups.every((g) => g.status === "NOT_CONFIGURED");

  // Attention items: a row appears only for a count that loaded, so a failed read
  // is never shown as "nothing to do".
  const overdueTaskCount = workflowStats.data?.overdueCount ?? 0;
  const dueTodayCount = workflowStats.data?.dueTodayCount ?? 0;
  const unreadCount = unreadMessages.isSuccess ? (unreadMessages.data?.unread_count ?? 0) : 0;
  const pendingAbsenceCount = pendingAbsences.isSuccess ? (pendingAbsences.data?.count ?? 0) : 0;
  const overdueInvoiceCount = overdueInvoices.isSuccess ? (overdueInvoices.data ?? 0) : 0;
  const submittedTimeEntryCount =
    !timeTrackingOff && submittedTimeEntries.isSuccess ? (submittedTimeEntries.data ?? 0) : 0;
  const understaffedCount = coverage.isSuccess
    ? (coverage.data?.warning_count ?? 0) + (coverage.data?.critical_count ?? 0)
    : 0;
  // The overdue balance of past-due invoices from the debtor summary (the
  // phone's source); without reconciliation the row keeps the invoice count.
  const summary = debtorSummary.isSuccess ? debtorSummary.data : undefined;
  const overdueCents = summary
    ? (summary.aging1To30Cents ?? 0) +
      (summary.aging31To60Cents ?? 0) +
      (summary.aging61To90Cents ?? 0) +
      (summary.aging90PlusCents ?? 0)
    : null;
  const overdueRowCount = summary?.overdueInvoiceCount ?? overdueInvoiceCount;
  // Groups short of staff on a coming day (tomorrow to +7), each group once.
  const aheadGroups = useMemo(() => {
    const ids = new Set<string>();
    let firstDate: string | undefined;
    for (const g of understaffedAhead.data?.groups ?? []) {
      if (g.status !== "CRITICAL" && g.status !== "WARNING") continue;
      if (g.group_id) ids.add(g.group_id);
      if (g.date && (!firstDate || g.date < firstDate)) firstDate = g.date;
    }
    return { count: ids.size, firstDate };
  }, [understaffedAhead.data]);
  const capacityItems: CapacityAttentionItem[] = (capacityAttention.data?.items ?? []).slice(
    0,
    DASHBOARD_CAPACITY_ATTENTION_ROWS,
  );
  // BF and SBU dates that run out within 60 days (the phone's window) or have
  // passed; the KmbB review meeting is not a subsidy date to renew.
  const expiringSubsidyCount = (expiringSubsidy.data?.items ?? []).filter(
    (r) => r.document !== "kmbb" && typeof r.days_remaining === "number" && r.days_remaining <= SUBSIDY_ATTENTION_DAYS,
  ).length;

  // Each source of the Attention card shows its rows as soon as it lands; a
  // failed source shows its own error in its rows' place, with a retry that
  // reads that source again (one summary block, #2193 A).
  const attentionLabels = {
    understaffed: i18n._({ id: "dashboard.attention.understaffed", message: "Understaffed groups today" }),
    understaffedAhead: i18n._({
      id: "dashboard.attention.understaffedAhead",
      message: "Understaffed groups in the next 7 days",
    }),
    overdueTasks: i18n._({ id: "dashboard.attention.overdueTasks", message: "Overdue tasks" }),
    unreadMessages: i18n._({ id: "dashboard.attention.unreadMessages", message: "Unread messages" }),
    pendingAbsences: i18n._({ id: "dashboard.attention.pendingAbsences", message: "Pending absence requests" }),
    pendingTimeEntries: i18n._({
      id: "dashboard.attention.pendingTimeEntries",
      message: "Time entries waiting for approval",
    }),
    overdueInvoices: i18n._({ id: "dashboard.attention.overdueInvoices", message: "Overdue invoices" }),
    capacity: i18n._(navItemLabel(NAV_ITEMS.find((item) => item.href === "/capacity")!)),
  };
  const attentionSources = [
    coverage,
    understaffedAhead,
    workflowStats,
    unreadMessages,
    pendingAbsences,
    ...(timeTrackingOff ? [] : [submittedTimeEntries]),
    overdueInvoices,
    capacityAttention,
  ];
  const attentionLanded = attentionSources.some((q) => q.isSuccess);
  const attentionPending = attentionSources.some((q) => q.isLoading);
  const attentionFailed = attentionSources.some((q) => q.isError);
  const attentionError = (source: { isError: boolean; error: unknown; refetch: () => unknown }, title: string) =>
    source.isError ? (
      <QueryErrorState
        error={source.error}
        onRetry={() => void source.refetch()}
        title={title}
        compact
        className="px-3"
      />
    ) : null;

  const hasAttentionItems =
    understaffedCount > 0 ||
    overdueTaskCount > 0 ||
    dueTodayCount > 0 ||
    unreadCount > 0 ||
    pendingAbsenceCount > 0 ||
    overdueRowCount > 0 ||
    submittedTimeEntryCount > 0 ||
    aheadGroups.count > 0 ||
    capacityItems.length > 0 ||
    expiringSubsidyCount > 0;

  // Changes summary totals
  const totalArrivals = changesSummary.data?.groups?.reduce(
    (sum, g) => sum + (g.arrivals_count ?? 0), 0
  ) ?? 0;
  const totalDepartures = changesSummary.data?.groups?.reduce(
    (sum, g) => sum + (g.departures_count ?? 0), 0
  ) ?? 0;

  // Upcoming events (next 5)
  const nextEvents = useMemo(() => {
    return [...(upcomingEvents.data ?? [])]
      .sort((a, b) => (a.startDateTime ?? a.startDate ?? "").localeCompare(b.startDateTime ?? b.startDate ?? ""))
      .slice(0, 5);
  }, [upcomingEvents.data]);

  // The capacity planner's own wording for an attention item, with the date written out.
  const capacityAttentionLabel = (item: CapacityAttentionItem): string => {
    const count = item.count ?? 0;
    const group = item.group_name ?? "";
    const date = item.effective_date ? formatDateDisplay(item.effective_date) : "";
    switch (item.type) {
      case "aging_out":
        return i18n._({
          id: "capacity.attention.item.agingOut",
          message:
            "{count, plural, one {# child ages out of {group} by {date}} other {# children age out of {group} by {date}}}",
          values: { count, group, date },
        });
      case "over_capacity":
        return i18n._({
          id: "capacity.attention.item.overCapacity",
          message:
            "{count, plural, one {{group} is over capacity on # day from {date}} other {{group} is over capacity on # days from {date}}}",
          values: { count, group, date },
        });
      case "out_of_sync":
        return i18n._({
          id: "capacity.attention.item.outOfSync",
          message:
            "{count, plural, one {# child is out of sync with their contract} other {# children are out of sync with their contracts}}",
          values: { count },
        });
      case "unresolved_conflict":
        return i18n._({
          id: "capacity.attention.item.unresolvedConflict",
          message:
            "{count, plural, one {# child has an unresolved allocation conflict} other {# children have unresolved allocation conflicts}}",
          values: { count },
        });
      default:
        return i18n._({
          id: "capacity.attention.item.unknown",
          message: "{count, plural, one {# item needs attention} other {# items need attention}}",
          values: { count },
        });
    }
  };

  const dayLabel = (date: Date) =>
    i18n._({
      id: "dashboard.dayLabel",
      message: "{weekday}, {date}",
      values: { weekday: formatWeekday(date, locale, "short"), date: formatDateMedium(date, locale) },
    });
  const capacityHref = `/capacity?date=${selectedDateStr}&calendarDate=${selectedDateStr}`;

  // One whole message per greeting, so each language places the name and the
  // comma itself; without a first name the plain greeting stands alone.
  const hour = new Date().getHours();
  const name = user?.firstName?.trim();
  let greeting: string;
  if (hour < 12) {
    greeting = name
      ? i18n._({ id: "dashboard.greeting.morning.named", message: "Good morning, {name}", values: { name } })
      : i18n._({ id: "dashboard.greeting.morning", message: "Good morning" });
  } else if (hour < 18) {
    greeting = name
      ? i18n._({ id: "dashboard.greeting.afternoon.named", message: "Good afternoon, {name}", values: { name } })
      : i18n._({ id: "dashboard.greeting.afternoon", message: "Good afternoon" });
  } else {
    greeting = name
      ? i18n._({ id: "dashboard.greeting.evening.named", message: "Good evening, {name}", values: { name } })
      : i18n._({ id: "dashboard.greeting.evening", message: "Good evening" });
  }

  const showQuickStats = canKitchen || canActivities || canSettlingIn;
  const showLiveStaffing = isSelectedToday && canShifts;

  return (
    <PageContainer width="list">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <PageHeader
          title={greeting}
          tabs={
            // Analytics is a cockpit-powered owner-only surface (issue #906); without it a
            // lone "Overview" tab says nothing, so non-admins get no tab row at all.
            isAdmin ? (
              <TabsList>
                <TabsTrigger value="overview">
                  <Trans id="cockpit.overview">Overview</Trans>
                </TabsTrigger>
                <TabsTrigger value="analytics">
                  <Trans id="cockpit.analytics">Analytics</Trans>
                </TabsTrigger>
              </TabsList>
            ) : undefined
          }
        />

        {/* Owner-only (issue #906): only mount the cockpit-powered analytics tab for admins,
            so a non-admin never lazy-loads it or fires its now-403 /api/v1/cockpit queries. */}
        {isAdmin && (
          <TabsContent value="analytics" className="mt-0">
            <Suspense
              fallback={
                <div className="flex h-full w-full items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              }
            >
              <DashboardAnalytics
                selectedChildcareId={selectedChildcareId}
                selectedGroupId={selectedGroupId}
                childcares={childcares.map((c) => ({ id: c.id || "", name: c.name || "" }))}
                allGroups={allGroups.map((g) => ({ id: g.id || "", name: g.name || "", childcare_id: g.childcare_id }))}
                onChildcareChange={handleChildcareChange}
                onGroupChange={handleGroupChange}
                onClearFilters={handleClearAllFilters}
              />
            </Suspense>
          </TabsContent>
        )}

        <TabsContent value="overview" className="mt-0 space-y-6">

      {/* Filter + Date navigation bar (matches Capacity Overview) */}
      <div className="flex items-center gap-2">
        {/* Filter popover */}
        <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={cn("h-10 w-10", hasActiveFilters && "border-primary text-primary")}
              aria-label={i18n._({ id: "dashboard.filter", message: "Filter" })}
            >
              <FilterIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="start">
            <div className="space-y-4">
              <h4 className="font-medium text-sm">
                {i18n._({ id: "dashboard.filter.title", message: "Filter dashboard" })}
              </h4>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground flex items-center gap-2">
                  <Building2Icon className="h-4 w-4" aria-hidden="true" />
                  {i18n._({ id: "capacity.filter.childcare", message: "Childcare" })}
                </label>
                <Select value={selectedChildcareId || "__all__"} onValueChange={handleChildcareChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={i18n._({ id: "capacity.filter.allChildcares", message: "All childcares" })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">
                      {i18n._({ id: "capacity.filter.allChildcares", message: "All childcares" })}
                    </SelectItem>
                    {childcares.map((cc) => (
                      /* A childcare row from the API always carries an id; the generated model marks it optional. */
                      <SelectItem key={cc.id} value={cc.id!}>
                        {cc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground flex items-center gap-2">
                  <UsersIcon className="h-4 w-4" aria-hidden="true" />
                  {i18n._({ id: "capacity.filter.group", message: "Group" })}
                </label>
                <Select value={selectedGroupId || "__all__"} onValueChange={handleGroupChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={i18n._({ id: "capacity.filter.allGroups", message: "All groups" })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">
                      {i18n._({ id: "capacity.filter.allGroups", message: "All groups" })}
                    </SelectItem>
                    {filteredGroupsForDropdown.map((g) => (
                      <SelectItem key={g.id} value={g.id || ""}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10"
          onClick={handlePrevDay}
          aria-label={i18n._({ id: "common.previousDay", message: "Previous day" })}
        >
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
        </Button>

        <div className="w-full sm:w-[210px]">
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <DatePicker
                value={selectedDate}
                onChange={handleDatePick}
                showClearButton={false}
              />
            </div>
            <Button
              variant="outline"
              className="shrink-0 h-10 px-3"
              onClick={handleToday}
              disabled={isSelectedToday}
            >
              <span className="text-xs font-medium">
                {i18n._({ id: "dashboard.today", message: "Today" })}
              </span>
            </Button>
          </div>
        </div>

        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10"
          onClick={handleNextDay}
          aria-label={i18n._({ id: "common.nextDay", message: "Next day" })}
        >
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </Button>

        {sources.updatedAt > 0 && (
          // The minute poll moves it: the figures below are this fresh.
          <p className="order-last ml-auto hidden text-xs text-muted-foreground tabular-nums sm:block" data-testid="dashboard-updated-at">
            {i18n._({
              id: "dashboard.updatedAt",
              message: "Updated {time}",
              values: { time: formatOrgTime(new Date(sources.updatedAt).toISOString()) },
            })}
          </p>
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAllFilters}
            className="text-muted-foreground hover:text-foreground"
          >
            <XIcon className="h-4 w-4 mr-1" aria-hidden="true" />
            <Trans id="dashboard.filter.clearAll">Clear All Filters</Trans>
          </Button>
        )}
      </div>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {selectedChildcareName && (
            <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
              <Building2Icon className="h-3 w-3" aria-hidden="true" />
              <span className="truncate max-w-32">{selectedChildcareName}</span>
              <button
                type="button"
                onClick={() => { setSelectedChildcareId(null); setSelectedGroupId(null); }}
                className="ml-1 rounded-sm hover:text-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={i18n._({ id: "analytics.chip.remove", message: "Remove {label}", values: { label: selectedChildcareName } })}
              >
                <XIcon className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          )}
          {selectedGroupName && (
            <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
              <UsersIcon className="h-3 w-3" aria-hidden="true" />
              <span className="truncate max-w-32">{selectedGroupName}</span>
              <button
                type="button"
                onClick={() => setSelectedGroupId(null)}
                className="ml-1 rounded-sm hover:text-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={i18n._({ id: "analytics.chip.remove", message: "Remove {label}", values: { label: selectedGroupName } })}
              >
                <XIcon className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      )}

      {isAdmin && <SetupChecklistCard checklist={setup} />}

      {/* KPI row */}
      <StatGrid>
        {canCapacity && (
          <StatCard
            label={<Trans id="dashboard.kpi.children">Children Today</Trans>}
            info={i18n._(FORMULA.dashboardChildrenToday)}
            icon={UsersIcon}
            href={capacityHref}
            isLoading={capacity.isLoading}
            isError={capacity.isError}
            onRetry={() => void capacity.refetch()}
            value={
              arrivals
                ? i18n._({
                    id: "dashboard.kpi.children.arrived",
                    message: "{present} of {expected}",
                    values: {
                      present: formatNumber(arrivals.present, locale),
                      expected: formatNumber(arrivals.expected, locale),
                    },
                  })
                : totalMaxHeadcount > 0
                  ? `${formatNumber(totalHeadcount, locale)} / ${formatNumber(totalMaxHeadcount, locale)}`
                  : formatNumber(totalHeadcount, locale)
            }
            hint={
              <>
                <span className="block">
                  {arrivals ? (
                    i18n._({ id: "dashboard.kpi.children.arrivedHint", message: "arrived of expected" })
                  ) : totalMaxHeadcount > 0 ? (
                    <Trans id="dashboard.kpi.children.desc">booked / places</Trans>
                  ) : (
                    <Trans id="dashboard.kpi.children.descTotal">children in groups</Trans>
                  )}
                </span>
                {totalMaxWeightedHeadcount > 0 && (
                  <span className="block tabular-nums">
                    {i18n._({
                      id: "dashboard.kpi.children.weighted",
                      message: "Weighted: {booked} / {places}",
                      values: {
                        booked: formatDecimal(totalWeightedHeadcount, locale, 1),
                        places: formatDecimal(totalMaxWeightedHeadcount, locale, 1),
                      },
                    })}
                  </span>
                )}
              </>
            }
          />
        )}
        {canShifts && (
          <StatCard
            label={<Trans id="dashboard.kpi.coverage">Staff Coverage</Trans>}
            info={i18n._(FORMULA.dashboardStaffCoverage)}
            icon={ShieldCheckIcon}
            href={`/shifts?tab=coverage&date=${selectedDateStr}`}
            isLoading={coverage.isLoading}
            isError={coverage.isError}
            onRetry={() => void coverage.refetch()}
            value={`${formatNumber(totalStaffOnDuty, locale)} / ${formatNumber(totalStaffRequired, locale, { maximumFractionDigits: 1 })}`}
            hint={
              staffingNotConfigured ? (
                i18n._({ id: "dashboard.kpi.coverage.notConfigured", message: "No staffing model saved" })
              ) : (
                <Trans id="dashboard.kpi.coverage.desc">on duty / required</Trans>
              )
            }
            // Nothing required (no staffing model, no children) is not "well staffed".
            tone={
              totalStaffRequired > 0
                ? totalStaffOnDuty >= totalStaffRequired ? "success" : "danger"
                : undefined
            }
          />
        )}
        {canCapacity && (
          <StatCard
            label={<Trans id="dashboard.kpi.occupancy">Occupancy</Trans>}
            info={i18n._(FORMULA.dashboardOccupancy)}
            icon={BarChart3Icon}
            href={capacityHref}
            isLoading={capacity.isLoading}
            isError={capacity.isError}
            onRetry={() => void capacity.refetch()}
            value={occupancy.percent === null ? "–" : formatPercent(occupancy.percent / 100, locale, 0)}
            title={i18n._({ id: "analytics.occupancy.basis", message: "Weighted children per weighted place" })}
            visual={
              occupancySpark.points.length > 1 ? (
                <MetricSparkline
                  points={occupancySpark.points}
                  format={(v) => formatPercent(v / 100, locale, 0)}
                  formatPeriod={(day) =>
                    i18n._({
                      id: "dashboard.kpi.occupancy.sparkWeek",
                      message: "Week from {date}",
                      values: { date: formatDateDisplay(day) },
                    })
                  }
                />
              ) : undefined
            }
            hint={
              <span className="tabular-nums">
                {[
                  occupancy.maxWeighted > 0
                    ? i18n._({
                        id: "dashboard.kpi.occupancy.weightedOf",
                        message: "Weighted {weighted} of {max}",
                        values: {
                          weighted: formatDecimal(occupancy.weighted, locale, 1),
                          max: formatDecimal(occupancy.maxWeighted, locale, 1),
                        },
                      })
                    : null,
                  i18n._({
                    id: "dashboard.kpi.occupancy.children",
                    message: "{count, plural, one {# child} other {# children}}",
                    values: { count: occupancy.children },
                  }),
                  occupancy.places > 0
                    ? i18n._({
                        id: "dashboard.kpi.occupancy.places",
                        message: "{count, plural, one {# place} other {# places}}",
                        values: { count: occupancy.places },
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            }
            tone={OCCUPANCY_TONE[occupancyAccent(occupancy.percent)]}
          />
        )}
        {canWorkflows && (
          <StatCard
            label={i18n._({ id: "nav.workflows", message: "Approvals" })}
            info={i18n._(FORMULA.dashboardApprovals)}
            icon={ClipboardListIcon}
            href={overdueTaskCount > 0 ? "/workflows?tab=overdue" : "/workflows"}
            isLoading={pendingCount.isLoading}
            isError={pendingCount.isError}
            onRetry={() => void pendingCount.refetch()}
            value={formatNumber(pendingCount.data?.count ?? 0, locale)}
            hint={
              <>
                <span className="block">
                  {i18n._({
                    id: "dashboard.kpi.approvals.waiting",
                    message: "{count, plural, one {# open} other {# open}}",
                    values: { count: pendingCount.data?.count ?? 0 },
                  })}
                </span>
                {overdueTaskCount > 0 && (
                  <span className="block text-destructive">
                    {i18n._({ id: "dashboard.kpi.tasks.overdue", message: "{count} overdue" }, { count: overdueTaskCount })}
                  </span>
                )}
              </>
            }
            tone={overdueTaskCount > 0 ? "danger" : undefined}
          />
        )}
      </StatGrid>

      {/* My metrics (#2175 C6): the cockpit metrics are owner-only, like Analytics. */}
      {isAdmin && (
        <MyMetricsBlock
          from={lastFullMonth}
          to={lastFullMonth}
          childcareId={selectedChildcareId}
          groupId={selectedGroupId}
          currency={currency}
        />
      )}

      {/* Group Overview + Attention Required + live staffing */}
      {/* Left: the groups and their occupancy over the year; right: what needs attention now. */}
      <div className="grid gap-4 lg:grid-cols-5">
        {canCapacity && (
          <div className="flex flex-col gap-4 lg:col-span-3">
            <GroupOverview
              groups={groupsWithLocation}
              childcares={childcareOptions}
              coverageGroups={coverage.data?.groups}
              isLoading={capacity.isLoading}
              isError={capacity.isError}
              error={capacity.error}
              onRetry={() => void capacity.refetch()}
              resolveServiceName={resolveServiceName}
              onNavigate={(groupId) => {
                const params = new URLSearchParams();
                if (groupId) params.set("groupId", groupId);
                params.set("date", selectedDateStr);
                params.set("calendarDate", selectedDateStr);
                navigate(`/capacity?${params.toString()}`);
              }}
            />
            {isAdmin && (
              <MetricPanelCard
                metricId="utilization"
                title={i18n._({ id: "dashboard.occupancyTrend.title", message: "Occupancy · last 12 months" })}
                from={trendWindow.from}
                to={trendWindow.to}
                scope={metricScope}
                currency={currency}
                height={200}
                detailHref={analyticsMetricHref("utilization", { childcare: selectedChildcareId, group: selectedGroupId })}
                enabled={isOverview}
              />
            )}
          </div>
        )}

        <div className={cn("flex flex-col gap-4", canCapacity ? "lg:col-span-2" : "lg:col-span-5")}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              <Trans id="dashboard.attention.title">Attention Required</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3">
            {!attentionLanded && attentionPending && !attentionFailed ? (
              <div className="space-y-3 px-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : !hasAttentionItems && !attentionFailed && !attentionPending ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <CheckCircle2Icon className="h-8 w-8 text-success-on-surface mb-2" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">
                  {setup.hasOpen ? (
                    i18n._({ id: "dashboard.attention.nothingYet", message: "Nothing to do yet" })
                  ) : (
                    <Trans id="dashboard.attention.allClear">All caught up!</Trans>
                  )}
                </p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {attentionError(coverage, attentionLabels.understaffed)}
                {understaffedCount > 0 && (
                  <AttentionItem
                    icon={<AlertTriangleIcon className="h-4 w-4" />}
                    label={attentionLabels.understaffed}
                    info={i18n._(FORMULA.dashboardUnderstaffedToday)}
                    count={understaffedCount}
                    severity="urgent"
                    href={`/shifts?tab=coverage&date=${selectedDateStr}`}
                  />
                )}
                {attentionError(understaffedAhead, attentionLabels.understaffedAhead)}
                {aheadGroups.count > 0 && (
                  <AttentionItem
                    testId="attention-understaffed-ahead"
                    icon={<AlertTriangleIcon className="h-4 w-4" />}
                    label={attentionLabels.understaffedAhead}
                    info={i18n._(FORMULA.dashboardUnderstaffedAhead)}
                    count={aheadGroups.count}
                    severity="warning"
                    href={`/shifts?tab=coverage&date=${aheadGroups.firstDate ?? aheadRange.from}`}
                  />
                )}
                {attentionError(workflowStats, attentionLabels.overdueTasks)}
                {overdueTaskCount > 0 && (
                  <AttentionItem
                    icon={<ClipboardListIcon className="h-4 w-4" />}
                    label={attentionLabels.overdueTasks}
                    info={i18n._(FORMULA.dashboardOverdueTasks)}
                    count={overdueTaskCount}
                    severity="urgent"
                    href="/workflows?tab=overdue"
                  />
                )}
                {dueTodayCount > 0 && (
                  <AttentionItem
                    icon={<ClipboardListIcon className="h-4 w-4" />}
                    label={
                      <Trans id="dashboard.attention.dueToday">
                        Tasks due today
                      </Trans>
                    }
                    count={dueTodayCount}
                    info={i18n._(FORMULA.dashboardDueToday)}
                    severity="warning"
                    href="/workflows"
                  />
                )}
                {attentionError(unreadMessages, attentionLabels.unreadMessages)}
                {unreadCount > 0 && (
                  <AttentionItem
                    icon={<MessageSquareIcon className="h-4 w-4" />}
                    label={attentionLabels.unreadMessages}
                    info={i18n._(FORMULA.dashboardUnreadMessages)}
                    count={unreadCount}
                    severity="warning"
                    href="/messaging"
                  />
                )}
                {attentionError(pendingAbsences, attentionLabels.pendingAbsences)}
                {pendingAbsenceCount > 0 && (
                  <AttentionItem
                    icon={<CalendarIcon className="h-4 w-4" />}
                    label={attentionLabels.pendingAbsences}
                    info={i18n._(FORMULA.dashboardPendingAbsences)}
                    count={pendingAbsenceCount}
                    severity="warning"
                    href="/shifts?tab=absences&filter=approval&forMe=1"
                  />
                )}
                {!timeTrackingOff && attentionError(submittedTimeEntries, attentionLabels.pendingTimeEntries)}
                {submittedTimeEntryCount > 0 && (
                  <AttentionItem
                    icon={<ClockIcon className="h-4 w-4" />}
                    label={attentionLabels.pendingTimeEntries}
                    info={i18n._(FORMULA.dashboardPendingTimeEntries)}
                    count={submittedTimeEntryCount}
                    severity="warning"
                    href="/time-tracking?view=approval"
                  />
                )}
                {attentionError(overdueInvoices, attentionLabels.overdueInvoices)}
                {overdueRowCount > 0 && (
                  <AttentionItem
                    testId="attention-overdue-invoices"
                    icon={<BanknoteIcon className="h-4 w-4" />}
                    label={
                      overdueCents !== null && overdueCents > 0
                        ? i18n._({
                            id: "dashboard.attention.overdueInvoicesAmount",
                            message: "Overdue invoices · {amount}",
                            values: { amount: formatMoneyCents(overdueCents, currency) },
                          })
                        : attentionLabels.overdueInvoices
                    }
                    count={overdueRowCount}
                    info={i18n._(FORMULA.dashboardOverdueInvoices)}
                    severity="warning"
                    href="/billing/invoices?status=OVERDUE&period=all"
                  />
                )}
                {expiringSubsidyCount > 0 && (
                  <AttentionItem
                    testId="attention-subsidy-expiring"
                    icon={<CalendarClockIcon className="h-4 w-4" />}
                    label={i18n._({
                      id: "dashboard.attention.subsidyDatesExpiring",
                      message: "Subsidy dates expiring within 60 days",
                    })}
                    count={expiringSubsidyCount}
                    info={i18n._(FORMULA.dashboardSubsidyExpiring)}
                    severity="warning"
                    href="#subsidy-expiry"
                  />
                )}
                {attentionError(capacityAttention, attentionLabels.capacity)}
                {capacityItems.map((item, i) => (
                  <AttentionItem
                    key={`${item.type ?? "item"}-${item.group_id ?? ""}-${item.rank ?? i}`}
                    testId="attention-capacity-item"
                    icon={<BarChart3Icon className="h-4 w-4" />}
                    label={
                      // The severity sits under the sentence, so a narrow column keeps the sentence readable.
                      <>
                        <span className="block">{capacityAttentionLabel(item)}</span>
                        <StatusBadge
                          className="mt-1"
                          tone={item.severity === "critical" ? "danger" : item.severity === "warning" ? "warning" : "info"}
                          label={
                            item.severity === "critical"
                              ? i18n._({ id: "capacity.attention.severity.critical", message: "Critical" })
                              : item.severity === "warning"
                                ? i18n._({ id: "capacity.attention.severity.warning", message: "Needs attention" })
                                : i18n._({ id: "dashboard.attention.severity.info", message: "Info" })
                          }
                        />
                      </>
                    }
                    severity={item.severity === "critical" ? "urgent" : item.severity === "warning" ? "warning" : "info"}
                    href={capacityAttentionHref(item)}
                    info={i18n._(FORMULA.dashboardCapacityItems)}
                  />
                ))}
                {/* A source still on its way: one row's room, so the card does not jump twice. */}
                {attentionPending && <Skeleton className="mx-3 h-8" data-testid="attention-pending" />}
              </div>
            )}
          </CardContent>
        </Card>

        {showLiveStaffing && (
          <LiveStaffingCard
            today={selectedDateStr}
            live={sources.liveCoverage}
            shifts={sources.shiftsToday}
            presence={sources.presence}
            groupId={selectedGroupId}
            inScope={scopeGroupIds ? inScope : undefined}
          />
        )}
        </div>
      </div>

      {/* Upcoming + Today's Stats + subsidy dates, one row of three from 1440px */}
      <div className="grid gap-4 lg:grid-cols-2 min-[1440px]:grid-cols-3">
        {/* Upcoming */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              <Trans id="dashboard.upcoming.title">Upcoming</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              {/* Birthdays: only admins see them, as before; the query is gated on
                  isAdmin above too. */}
              {isAdmin && <UpcomingBirthdays data={birthdays.data} days={BIRTHDAY_WINDOW_DAYS} />}

              {/* Arrivals & Departures */}
              {(totalArrivals > 0 || totalDepartures > 0) && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <UsersIcon className="h-3 w-3" aria-hidden="true" />
                    <Trans id="dashboard.upcoming.changes">Roster Changes (30 days)</Trans>
                    <FormulaTip content={i18n._(FORMULA.dashboardRosterChanges)} className="normal-case tracking-normal" />
                  </h4>
                  {totalArrivals > 0 && (
                    <div className="flex items-center gap-2 text-sm pl-1">
                      <UserPlusIcon className="h-3.5 w-3.5 text-success-on-surface flex-shrink-0" aria-hidden="true" />
                      <span>
                        {i18n._({ id: "dashboard.upcoming.arrivals", message: "{count} new children arriving" }, { count: totalArrivals })}
                      </span>
                    </div>
                  )}
                  {totalDepartures > 0 && (
                    <div className="flex items-center gap-2 text-sm pl-1">
                      <UserMinusIcon className="h-3.5 w-3.5 text-destructive flex-shrink-0" aria-hidden="true" />
                      <span>
                        {i18n._({ id: "dashboard.upcoming.departures", message: "{count} children departing" }, { count: totalDepartures })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Calendar Events */}
              {canCalendar && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <CalendarIcon className="h-3 w-3" aria-hidden="true" />
                  <Trans id="dashboard.upcoming.events">Events</Trans>
                </h4>
                {nextEvents.length > 0 ? (
                  nextEvents.map((event) => {
                    const eventDate = event.startDateTime
                      ? parseISO(event.startDateTime)
                      : event.startDate
                        ? parseISO(event.startDate)
                        : null;
                    return (
                      <Link
                        key={event.id}
                        to={`/calendar?eventId=${event.id}`}
                        className="flex items-center gap-2 text-sm w-full text-left hover:bg-muted/50 rounded px-1 py-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <CalendarIcon className="h-3.5 w-3.5 text-info-on-surface flex-shrink-0" aria-hidden="true" />
                        <span className="font-medium truncate">{event.title}</span>
                        {eventDate && (
                          <span className="text-muted-foreground ml-auto flex-shrink-0">
                            {dayLabel(eventDate)}
                          </span>
                        )}
                      </Link>
                    );
                  })
                ) : (
                  <p className="text-xs text-muted-foreground pl-1">
                    <Trans id="dashboard.upcoming.noEvents">No events in the next 30 days</Trans>
                  </p>
                )}
              </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Today's Quick Stats */}
        {showQuickStats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              <Trans id="dashboard.quickStats.title">Today&apos;s Overview</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              {canKitchen && (
                <QuickStat
                  href={`/kitchen?date=${selectedDateStr}`}
                  icon={<UtensilsIcon className="h-5 w-5" />}
                  iconClassName="bg-warning-muted text-warning-on-surface"
                  label={<Trans id="dashboard.quickStats.meals">Meals Today</Trans>}
                  info={i18n._(FORMULA.dashboardMeals)}
                  isLoading={capacity.isLoading}
                  value={capacity.isSuccess && totalHeadcount > 0 ? formatNumber(totalHeadcount, locale) : "–"}
                />
              )}
              {canActivities && (
                <QuickStat
                  href={`/activities?date_from=${selectedDateStr}&date_to=${selectedDateStr}`}
                  icon={<ActivityIcon className="h-5 w-5" />}
                  iconClassName="bg-info-muted text-info-on-surface"
                  label={<Trans id="dashboard.quickStats.activities">Activities Logged</Trans>}
                  info={i18n._(FORMULA.dashboardActivities)}
                  isLoading={todayActivities.isLoading}
                  value={todayActivities.isSuccess ? formatNumber(todayActivities.data?.total ?? 0, locale) : "–"}
                />
              )}
              {canSettlingIn && (
                <QuickStat
                  href="/settling-in"
                  icon={<BabyIcon className="h-5 w-5" />}
                  iconClassName="bg-primary/10 text-primary"
                  label={<Trans id="dashboard.quickStats.settlingIn">Active Settling-In Plans</Trans>}
                  info={i18n._(FORMULA.dashboardSettlingIn)}
                  isLoading={settlingIn.isLoading}
                  value={settlingIn.isSuccess ? formatNumber(settlingInRunning, locale) : "–"}
                />
              )}
            </div>
          </CardContent>
        </Card>
        )}
        {/* BF Expiring Soon widget */}
        <BfExpiringWidget
          daysAhead={SUBSIDY_WIDGET_DAYS}
          enabled={sources.subsidyReady}
          className="lg:col-span-2 min-[1440px]:col-span-1"
        />
      </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

interface QuickStatProps {
  href: string;
  icon: React.ReactNode;
  iconClassName: string;
  label: React.ReactNode;
  value: React.ReactNode;
  isLoading: boolean;
  /** How the figure is calculated: an info icon beside the link. */
  info?: string;
}

/** One line of "Today's Overview": a tinted icon, the label and the figure, opening its page. */
function QuickStat({ href, icon, iconClassName, label, value, isLoading, info }: QuickStatProps) {
  const link = (
    <Link
      to={href}
      className="flex items-center gap-4 rounded-md p-3 hover:bg-muted/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className={cn("flex h-10 w-10 items-center justify-center rounded-lg", iconClassName)} aria-hidden="true">
        {icon}
      </span>
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-2xl font-bold tabular-nums">
          {isLoading ? <Skeleton className="h-8 w-12" /> : value}
        </div>
      </div>
    </Link>
  );
  if (!info) return link;
  return (
    <div className="flex items-start gap-1">
      <div className="min-w-0 flex-1">{link}</div>
      <FormulaTip content={info} className="mt-3" />
    </div>
  );
}
